import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  ExternalLink, 
  MessageSquare, 
  Image as ImageIcon, 
  Video, 
  Code as CodeIcon, 
  Layers,
  Search,
  Heart,
  TrendingUp,
  RefreshCw,
  AlertCircle
} from 'lucide-react';
import { Tool } from '../types';

const API_BASE = `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/tools`;

const categoryMap = {
  text: { label: 'Text', icon: <MessageSquare size={20} />, class: 'text-bg' },
  image: { label: 'Image', icon: <ImageIcon size={20} />, class: 'image-bg' },
  video: { label: 'Video', icon: <Video size={20} />, class: 'video-bg' },
  code: { label: 'Code', icon: <CodeIcon size={20} />, class: 'code-bg' },
  other: { label: 'Other', icon: <Layers size={20} />, class: 'other-bg' },
};

const getToolDesc = (tool: any, lang: string) => {
  if (!tool?.description) return '';
  if (typeof tool.description === 'string') return tool.description;
  const l = (lang || 'en').substring(0, 2);
  return tool.description[l] || tool.description.en || '';
};

const Home: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [tools, setTools] = useState<Tool[]>([]);
  const [filteredTools, setFilteredTools] = useState<Tool[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [favorites, setFavorites] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<'name' | 'popularity'>('name');

  useEffect(() => {
    document.title = t('title', 'AI Directory - Best Artificial Intelligence Tools Directory');
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', t('subtitle', "Discover AI Directory, the best directory to find, compare and organize the best Artificial Intelligence tools."));
    }

    fetchTools();
    try {
      const savedFavs = localStorage.getItem('fav_tools');
      if (savedFavs) setFavorites(JSON.parse(savedFavs));
    } catch (e) {
      console.error('Error loading favorites:', e);
    }
  }, [t]);

  const fetchTools = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(API_BASE);
      setTools(Array.isArray(response.data) ? response.data : []);
      setLoading(false);
    } catch (error: any) {
      console.error('Error fetching tools:', error);
      setError("Unable to connect to the server.");
      setLoading(false);
    }
  };

  const toggleFavorite = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    const newFavs = favorites.includes(id) 
      ? favorites.filter(f => f !== id) 
      : [...favorites, id];
    setFavorites(newFavs);
    localStorage.setItem('fav_tools', JSON.stringify(newFavs));
  };

  const getStats = (tool: Tool) => {
    if (!tool.clickStats || !Array.isArray(tool.clickStats)) return 0;
    return tool.clickStats.reduce((acc, curr) => acc + (curr.count || 0), 0);
  };

  useEffect(() => {
    let result = [...tools];
    
    if (activeCategory === 'favorites') {
      result = result.filter(tool => favorites.includes(tool._id!));
    } else if (activeCategory !== 'all') {
      result = result.filter(tool => tool.category === activeCategory);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(tool => {
        const desc = getToolDesc(tool, i18n.language);
        return tool.name.toLowerCase().includes(query) || (desc && desc.toLowerCase().includes(query));
      });
    }

    if (sortBy === 'popularity') {
      result.sort((a, b) => getStats(b) - getStats(a));
    } else {
      result.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }

    setFilteredTools(result);
  }, [activeCategory, searchQuery, tools, favorites, sortBy, i18n.language]);

  return (
    <div className="home-page">
      <header className="hero-section">
        <h1>{t('title')}</h1>
        <p className="hero-subtitle">
          {t('subtitle')}
        </p>
      </header>

      <section className="seo-intro" style={{ maxWidth: '800px', margin: '0 auto 2rem', textAlign: 'center', padding: '0 1rem' }}>
        <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--text-main)' }}>{t('hero_h2', 'Find the right AI for your project')}</h2>
        <p style={{ color: 'var(--text-muted)', lineHeight: '1.6' }}>
          {t('hero_desc', 'Our directory lists the most powerful Artificial Intelligence tools on the market...')}
        </p>
      </section>

      <div className="controls">
        <div className="search-bar">
          <Search size={20} />
          <input 
            type="text" 
            placeholder={t('search_placeholder')} 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="filter-container">
          <button 
            className={`filter-btn ${activeCategory === 'all' ? 'active' : ''}`}
            onClick={() => setActiveCategory('all')}
          >
            {t('categories.all')}
          </button>
          <button 
            className={`filter-btn ${activeCategory === 'favorites' ? 'active' : ''}`}
            onClick={() => setActiveCategory('favorites')}
          >
            <Heart size={16} fill={activeCategory === 'favorites' ? 'currentColor' : 'none'} /> {t('categories.favorites', 'Favorites')}
          </button>
          {Object.entries(categoryMap).map(([key, value]) => (
            <button 
              key={key}
              className={`filter-btn ${activeCategory === key ? 'active' : ''}`}
              onClick={() => setActiveCategory(key)}
            >
              {t(`categories.${key}` as any, value.label)}
            </button>
          ))}
        </div>

        <div className="sort-container">
          <span className="sort-label">{t('sort_by', 'Sort by:')}</span>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value as any)}>
            <option value="name">{t('sort_name', 'Name (A-Z)')}</option>
            <option value="popularity">{t('sort_popularity', 'Popularity')}</option>
          </select>
          <button className="refresh-btn" onClick={fetchTools} title="Refresh">
            <RefreshCw size={18} className={loading ? 'spinning' : ''} />
          </button>
        </div>
      </div>

      <main>
        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Initializing resources...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <AlertCircle size={48} color="#ef4444" />
            <p>{error}</p>
            <button className="retry-btn" onClick={fetchTools}>Retry</button>
          </div>
        ) : (
          <div className="tool-grid">
            {filteredTools.map((tool) => {
              const cat = categoryMap[tool.category as keyof typeof categoryMap] || categoryMap.other;
              const isFav = favorites.includes(tool._id!);
              return (
                <div key={tool._id} className="tool-card clickable" onClick={() => window.open(`/tool/${tool._id}`, '_blank')}>
                  <button 
                    className={`fav-toggle ${isFav ? 'active' : ''}`}
                    onClick={(e) => toggleFavorite(e, tool._id!)}
                  >
                    <Heart size={20} fill={isFav ? '#ef4444' : 'none'} />
                  </button>
                  <div className="card-header">
                    <div className={`category-icon ${cat.class}`}>
                      {cat.icon}
                    </div>
                    <h3>{tool.name}</h3>
                  </div>
                  <p>{getToolDesc(tool, i18n.language)}</p>
                  <div className="popularity-badge">
                    <TrendingUp size={12} /> {getStats(tool)} {t('clicks', 'clicks')}
                  </div>
                  <div className="card-footer">
                    <span className={`badge ${cat.class}`}>{t(`categories.${tool.category}` as any, cat.label)}</span>
                    <span className="view-details">{t('tool.discover')} <ExternalLink size={14} /></span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default Home;
